import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, Filter, Calendar, Users, TrendingDown, AlertCircle, ChevronDown, ChevronRight, Wallet } from "lucide-react";

const DEPARTMENTS = ["Estimation", "Human Resources", "Management", "Projects", "Sales", "Accounts and Finance", "PMO", "Training"];
const BRANCHES = ["Chennai", "Hyderabad"];

const pad2 = (n) => String(n).padStart(2, "0");

export default function LeaveReports({ user }) {
  const now = new Date();
  const currentYear = now.getFullYear();

  // Filters
  const [periodType, setPeriodType] = useState("month");
  const [periodValue, setPeriodValue] = useState(`${currentYear}-${pad2(now.getMonth() + 1)}`);
  const [department, setDepartment] = useState("all");
  const [branch, setBranch] = useState("all");
  const [employeeSearch, setEmployeeSearch] = useState("");

  // Data
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [detailData, setDetailData] = useState({});
  const [detailLoading, setDetailLoading] = useState({});

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    p.set("period_type", periodType);
    p.set("period_value", periodValue);
    if (department && department !== "all") p.set("department", department);
    if (branch && branch !== "all") p.set("branch", branch);
    if (employeeSearch.trim()) p.set("employee_search", employeeSearch.trim());
    return p.toString();
  }, [periodType, periodValue, department, branch, employeeSearch]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leave/reports/summary?${buildQuery()}`);
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to load leave report");
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Reset expanded rows when filters change (data may shift)
  useEffect(() => {
    setExpandedRows({});
    setDetailData({});
  }, [periodType, periodValue, department, branch, employeeSearch]);

  const toggleRow = async (empId) => {
    const isOpen = !!expandedRows[empId];
    setExpandedRows((prev) => ({ ...prev, [empId]: !isOpen }));
    if (!isOpen && !detailData[empId]) {
      setDetailLoading((p) => ({ ...p, [empId]: true }));
      try {
        const p = new URLSearchParams();
        p.set("employee_id", empId);
        p.set("period_type", periodType);
        p.set("period_value", periodValue);
        const res = await api.get(`/leave/reports/employee-detail?${p.toString()}`);
        setDetailData((prev) => ({ ...prev, [empId]: res.data }));
      } catch (err) {
        toast.error("Failed to load details");
      } finally {
        setDetailLoading((p) => ({ ...p, [empId]: false }));
      }
    }
  };

  const statusBadgeClass = (status) => {
    switch (status) {
      case "approved": return "bg-green-100 text-green-800";
      case "rejected": return "bg-red-100 text-red-800";
      case "cancelled": return "bg-gray-200 text-gray-700";
      case "cancellation_requested": return "bg-orange-100 text-orange-800";
      case "pending": return "bg-amber-100 text-amber-800";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const statusLabel = (status) =>
    status === "cancellation_requested" ? "Cancel Pending" : status?.charAt(0).toUpperCase() + status?.slice(1);

  // When the period type changes, set a sensible default value
  const onChangePeriodType = (t) => {
    setPeriodType(t);
    if (t === "month") {
      setPeriodValue(`${currentYear}-${pad2(now.getMonth() + 1)}`);
    } else if (t === "quarter") {
      const q = Math.floor(now.getMonth() / 3) + 1;
      setPeriodValue(`${currentYear}-Q${q}`);
    } else {
      setPeriodValue(`${currentYear}`);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/leave/reports/summary/export?${buildQuery()}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leave_report_${periodType}_${periodValue}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (err) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  // Helpers for period value picker
  const renderPeriodValuePicker = () => {
    if (periodType === "month") {
      return (
        <Input
          type="month"
          value={periodValue}
          onChange={(e) => setPeriodValue(e.target.value)}
          className="bg-white"
          data-testid="leave-report-month-input"
        />
      );
    }
    if (periodType === "quarter") {
      const [yr, q] = periodValue.split("-");
      return (
        <div className="flex gap-2">
          <Input
            type="number"
            min="2020"
            max="2099"
            value={yr || currentYear}
            onChange={(e) => setPeriodValue(`${e.target.value}-${q || "Q1"}`)}
            className="bg-white w-24"
          />
          <Select value={q || "Q1"} onValueChange={(v) => setPeriodValue(`${yr || currentYear}-${v}`)}>
            <SelectTrigger className="bg-white w-24" data-testid="leave-report-quarter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Q1">Q1</SelectItem>
              <SelectItem value="Q2">Q2</SelectItem>
              <SelectItem value="Q3">Q3</SelectItem>
              <SelectItem value="Q4">Q4</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    }
    return (
      <Input
        type="number"
        min="2020"
        max="2099"
        value={periodValue}
        onChange={(e) => setPeriodValue(e.target.value)}
        className="bg-white w-32"
      />
    );
  };

  const scopeLabel = {
    all: "Showing: all employees",
    team: "Showing: your team",
    self: "Showing: your data only"
  }[report?.scope] || "";

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-md3-display-small font-semibold text-md3-on-surface">Leave Reports</h1>
            <p className="text-md3-on-surface-variant text-sm">Monthly / Quarterly / Yearly leave usage and balances · {scopeLabel}</p>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting || !report}
            className="bg-md3-primary text-md3-on-primary rounded-md3-full"
            data-testid="leave-report-export-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export Excel
          </Button>
        </div>

        {/* Filter Bar */}
        <Card className="border-0 shadow-md3-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-md3-title-medium flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-xs">Period</Label>
                <Select value={periodType} onValueChange={onChangePeriodType}>
                  <SelectTrigger className="bg-white mt-1" data-testid="leave-report-period-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Value</Label>
                <div className="mt-1">{renderPeriodValuePicker()}</div>
              </div>
              <div>
                <Label className="text-xs">Department</Label>
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger className="bg-white mt-1" data-testid="leave-report-department-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Branch</Label>
                <Select value={branch} onValueChange={setBranch}>
                  <SelectTrigger className="bg-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Search Employee</Label>
                <Input
                  placeholder="Name or ID"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="bg-white mt-1"
                  data-testid="leave-report-search-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-md3-primary" />
          </div>
        ) : !report ? (
          <p className="text-md3-on-surface-variant text-sm">No data available.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-blue-500 shadow-md3-1">
                <CardContent className="pt-4">
                  <p className="text-xs text-md3-on-surface-variant flex items-center gap-1"><Users className="h-3 w-3" /> Employees</p>
                  <p className="text-2xl font-bold text-blue-700">{report.summary.total_employees}</p>
                  <p className="text-xs text-md3-on-surface-variant">{report.summary.employees_with_leave} with leave</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-emerald-500 shadow-md3-1">
                <CardContent className="pt-4">
                  <p className="text-xs text-md3-on-surface-variant flex items-center gap-1"><Calendar className="h-3 w-3" /> SL · CL · EL</p>
                  <p className="text-2xl font-bold text-emerald-700">{(report.summary.total_sl_taken + report.summary.total_cl_taken + report.summary.total_el_taken).toFixed(1)}</p>
                  <p className="text-xs text-md3-on-surface-variant">SL {report.summary.total_sl_taken} · CL {report.summary.total_cl_taken} · EL {report.summary.total_el_taken}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500 shadow-md3-1">
                <CardContent className="pt-4">
                  <p className="text-xs text-md3-on-surface-variant flex items-center gap-1"><AlertCircle className="h-3 w-3" /> LOP Days</p>
                  <p className="text-2xl font-bold text-red-700">{report.summary.total_lop_days}</p>
                  <p className="text-xs text-md3-on-surface-variant">Loss of Pay (period)</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500 shadow-md3-1">
                <CardContent className="pt-4">
                  <p className="text-xs text-md3-on-surface-variant flex items-center gap-1"><TrendingDown className="h-3 w-3" /> EL Encashed</p>
                  <p className="text-2xl font-bold text-amber-700">{report.summary.total_el_encashed}</p>
                  <p className="text-xs text-md3-on-surface-variant">Days encashed</p>
                </CardContent>
              </Card>
            </div>

            {/* Top leave takers */}
            {report.summary.top_leave_takers?.length > 0 && (
              <Card className="border-0 shadow-md3-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-md3-title-medium">Top Leave Takers — {report.period.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {report.summary.top_leave_takers.map((t) => (
                      <Badge key={t.employee_id} className="bg-md3-primary-container text-md3-on-primary-container">
                        {t.employee_name} · {t.total_days}d
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Detail table */}
            <Card className="border-0 shadow-md3-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-md3-title-medium">
                  Per-Employee Breakdown · {report.rows.length} employees · {report.period.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-28">Emp ID</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-right">SL Taken</TableHead>
                      <TableHead className="text-right">SL Bal</TableHead>
                      <TableHead className="text-right">CL Taken</TableHead>
                      <TableHead className="text-right">CL Bal</TableHead>
                      <TableHead className="text-right">EL Taken</TableHead>
                      <TableHead className="text-right">EL Bal</TableHead>
                      <TableHead className="text-right">Mat. Taken</TableHead>
                      <TableHead className="text-right">Mat. Bal</TableHead>
                      <TableHead className="text-right text-red-700">LOP</TableHead>
                      <TableHead className="text-right text-amber-700">EL Encashed</TableHead>
                      <TableHead className="text-right font-semibold">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={16} className="text-center py-8 text-md3-on-surface-variant">
                          No employees match the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.rows.flatMap((r) => {
                        const isOpen = !!expandedRows[r.employee_id];
                        const detail = detailData[r.employee_id];
                        const isLoading = !!detailLoading[r.employee_id];
                        const rows = [
                          <TableRow key={r.employee_id} data-testid={`leave-report-row-${r.employee_id}`} className={isOpen ? "bg-md3-surface-container-low" : undefined}>
                            <TableCell>
                              <button
                                type="button"
                                onClick={() => toggleRow(r.employee_id)}
                                className="text-md3-on-surface-variant hover:text-md3-primary"
                                data-testid={`leave-report-expand-${r.employee_id}`}
                                aria-label={isOpen ? "Collapse" : "Expand"}
                              >
                                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{r.employee_id}</TableCell>
                            <TableCell className="font-medium">{r.employee_name}</TableCell>
                            <TableCell>{r.department}</TableCell>
                            <TableCell>{r.branch}</TableCell>
                            <TableCell className="text-right">{r.sl_taken}</TableCell>
                            <TableCell className="text-right text-md3-on-surface-variant">{r.sl_balance}</TableCell>
                            <TableCell className="text-right">{r.cl_taken}</TableCell>
                            <TableCell className="text-right text-md3-on-surface-variant">{r.cl_balance}</TableCell>
                            <TableCell className="text-right">{r.el_taken}</TableCell>
                            <TableCell className="text-right text-md3-on-surface-variant">{r.el_balance}</TableCell>
                            <TableCell className="text-right">{r.maternity_taken}</TableCell>
                            <TableCell className="text-right text-md3-on-surface-variant">{r.maternity_balance}</TableCell>
                            <TableCell className="text-right text-red-700 font-medium">{r.lop_days || "-"}</TableCell>
                            <TableCell className="text-right text-amber-700 font-medium">{r.el_encashed || "-"}</TableCell>
                            <TableCell className="text-right font-semibold">{r.total_taken}</TableCell>
                          </TableRow>
                        ];
                        if (isOpen) {
                          rows.push(
                            <TableRow key={`${r.employee_id}-detail`} className="bg-md3-surface-container-low">
                              <TableCell colSpan={16} className="p-0">
                                <div className="bg-md3-surface-container-low p-4 border-t border-b border-md3-outline-variant space-y-4">
                                  {isLoading ? (
                                    <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
                                  ) : !detail ? (
                                    <p className="text-xs text-md3-on-surface-variant">No data.</p>
                                  ) : (
                                    <>
                                      {/* Leave History */}
                                      <div>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-md3-on-surface-variant mb-2">
                                          Leave History · {detail.leave_applications.length} application(s) in {detail.period.label}
                                        </p>
                                        <div className="bg-white rounded-md3-sm border border-md3-outline-variant overflow-hidden">
                                          <table className="w-full text-xs">
                                            <thead className="bg-md3-surface-container">
                                              <tr className="text-left">
                                                <th className="px-3 py-2 font-semibold">Dates</th>
                                                <th className="px-3 py-2 font-semibold">Type</th>
                                                <th className="px-3 py-2 font-semibold text-right">Days</th>
                                                <th className="px-3 py-2 font-semibold">Status</th>
                                                <th className="px-3 py-2 font-semibold">Approved By</th>
                                                <th className="px-3 py-2 font-semibold">Reason</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {detail.leave_applications.length === 0 ? (
                                                <tr><td colSpan={6} className="px-3 py-3 text-center text-md3-on-surface-variant">No leaves in this period</td></tr>
                                              ) : detail.leave_applications.map((a) => (
                                                <tr key={a.id} className="border-t border-md3-outline-variant">
                                                  <td className="px-3 py-2">
                                                    {a.start_date}
                                                    {a.start_date !== a.end_date && <> → {a.end_date}</>}
                                                    {a.is_half_day && <Badge variant="outline" className="ml-1 text-[10px] py-0">Half</Badge>}
                                                  </td>
                                                  <td className="px-3 py-2 capitalize">
                                                    {a.leave_type}
                                                    {a.is_lop && <Badge className="ml-1 bg-red-100 text-red-700 text-[10px] py-0">LOP</Badge>}
                                                  </td>
                                                  <td className="px-3 py-2 text-right font-medium">{a.total_days}</td>
                                                  <td className="px-3 py-2">
                                                    <Badge className={statusBadgeClass(a.status)}>{statusLabel(a.status)}</Badge>
                                                  </td>
                                                  <td className="px-3 py-2 text-md3-on-surface-variant">{a.approved_by || "-"}</td>
                                                  <td className="px-3 py-2 text-md3-on-surface-variant max-w-xs truncate" title={a.reason}>{a.reason}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>

                                      {/* Encashment History */}
                                      <div>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-md3-on-surface-variant mb-2 flex items-center gap-1">
                                          <Wallet className="h-3 w-3" /> Encashment History · {detail.encashment_requests.length} request(s)
                                        </p>
                                        <div className="bg-white rounded-md3-sm border border-md3-outline-variant overflow-hidden">
                                          <table className="w-full text-xs">
                                            <thead className="bg-md3-surface-container">
                                              <tr className="text-left">
                                                <th className="px-3 py-2 font-semibold">Applied / Date</th>
                                                <th className="px-3 py-2 font-semibold text-right">Days</th>
                                                <th className="px-3 py-2 font-semibold">Status</th>
                                                <th className="px-3 py-2 font-semibold">Approved By</th>
                                                <th className="px-3 py-2 font-semibold">Reason / Note</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {detail.encashment_requests.length === 0 && detail.encashments.length === 0 ? (
                                                <tr><td colSpan={5} className="px-3 py-3 text-center text-md3-on-surface-variant">No encashment activity</td></tr>
                                              ) : detail.encashment_requests.map((e) => (
                                                <tr key={e.id} className="border-t border-md3-outline-variant">
                                                  <td className="px-3 py-2">{e.applied_at ? new Date(e.applied_at).toLocaleDateString() : "-"}</td>
                                                  <td className="px-3 py-2 text-right font-medium">{e.days}</td>
                                                  <td className="px-3 py-2"><Badge className={statusBadgeClass(e.status)}>{statusLabel(e.status)}</Badge></td>
                                                  <td className="px-3 py-2 text-md3-on-surface-variant">{e.approved_by || "-"}</td>
                                                  <td className="px-3 py-2 text-md3-on-surface-variant max-w-xs truncate" title={e.reason}>
                                                    {e.status === "rejected" ? e.rejection_reason : e.reason}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return rows;
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
